import React, { useState } from 'react'
import { Stack, Chip, SvgIcon, Button, DialogContent, Typography, Box, Divider } from '@mui/material'
import { common } from '@mui/material/colors'
import { useTheme, darken } from '@mui/material/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { ReactComponent as CalendarRecurring } from 'assets/CalendarRecurringIcon.svg'
import { ReactComponent as CalendarOnRequestIcon } from 'assets/CalendarOnRequestIcon.svg'
import { ReactComponent as InvoicedIcon } from 'assets/InvoicedIcon.svg'
import { ReactComponent as ExceptionsIcon } from 'assets/ExceptionsIcon.svg'
import { ReactComponent as DisposalTicketIcon } from 'assets/DisposalTicketIcon.svg'
import { ReactComponent as CurrencyDollarIcon } from 'assets/CurrencyDollarIcon.svg'
import { ReactComponent as GeneralFeeIcon } from 'assets/GeneralFeeIcon.svg'
import { ReactComponent as WorkOrderNote } from 'assets/note/WorkOrderNote.svg'
import { ReactComponent as UnassignedRouteIcon } from 'assets/UnassignedRouteIcon.svg'
import { WORK_ORDER_STATUS_COLORS, WORK_ORDER_STATUS_ICONS } from 'settings/constants/workOrder'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import RouteAvatar from 'components/route-manager/RouteDetailsModal/RouteAvatar/RouteAvatar'
import T from 'T'

const StatusLegend = () => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const statusItems = [
    {
      icon: WORK_ORDER_STATUS_ICONS.Scheduled,
      label: T.SCHEDULED,
      color: WORK_ORDER_STATUS_COLORS.Scheduled,
      iconColor: theme.palette.grey[600],
      description: T.SCHEDULED_DESCRIPTION,
    },
    {
      icon: WORK_ORDER_STATUS_ICONS.InProgress,
      label: T.IN_PROGRESS,
      color: WORK_ORDER_STATUS_COLORS.InProgress,
      iconColor: theme.palette.primary.main,
      description: T.IN_PROGRESS_DESCRIPTION,
    },
    {
      icon: WORK_ORDER_STATUS_ICONS.Serviced,
      label: T.SERVICED,
      color: WORK_ORDER_STATUS_COLORS.Serviced,
      iconColor: '#4CAF50',
      description: T.SERVICED_DESCRIPTION,
    },
    {
      icon: WORK_ORDER_STATUS_ICONS.Skipped,
      label: T.SKIPPED,
      color: WORK_ORDER_STATUS_COLORS.Skipped,
      iconColor: '#FFA726',
      description: T.SKIPPED_DESCRIPTION,
    },
    {
      icon: WORK_ORDER_STATUS_ICONS.Canceled,
      label: T.CANCELLED,
      color: WORK_ORDER_STATUS_COLORS.Canceled,
      iconColor: theme.palette.error.main,
      description: T.CANCELLED_DESCRIPTION,
    },
    {
      icon: WORK_ORDER_STATUS_ICONS.Hold,
      label: T.HOLD,
      color: WORK_ORDER_STATUS_COLORS.Hold,
      iconColor: theme.palette.secondary.main,
      isSvg: true,
      description: T.HOLD_DESCRIPTION,
    },
    {
      icon: WORK_ORDER_STATUS_ICONS.Suspended,
      label: T.SUSPENDED,
      color: WORK_ORDER_STATUS_COLORS.Suspended,
      iconColor: common.white,
      textColor: common.white,
      description: T.SUSPENDED_DESCRIPTION,
    },
  ]

  const calendarIcons = [
    {
      icon: CalendarRecurring,
      label: T.RECURRING_SERVICE,
      description: T.RECURRING_SERVICE_DESCRIPTION,
      color: theme.palette.primary.main,
      isSvg: true,
    },
    {
      icon: CalendarOnRequestIcon,
      label: T.ON_REQUEST_SERVICE,
      description: T.ON_REQUEST_SERVICE_DESCRIPTION,
      color: '#FFA726',
      isSvg: true,
    },
    {
      icon: AssignmentIcon,
      label: T.WORK_ORDERS,
      description: T.WORK_ORDERS_DESCRIPTION,
      color: theme.palette.grey[700],
    },
    {
      icon: CurrencyDollarIcon,
      label: T.PAYMENTS_MADE,
      description: T.PAYMENTS_MADE_DESCRIPTION,
      color: theme.palette.grey[600],
      isSvg: true,
    },
    {
      icon: InvoicedIcon,
      label: T.INVOICE_SENT,
      description: T.INVOICE_SENT_DESCRIPTION,
      color: '#66BB6A',
      isSvg: true,
    },
    {
      icon: ExceptionsIcon,
      label: T.EXCEPTIONS,
      description: T.EXCEPTIONS_DESCRIPTION,
      color: theme.palette.error.main,
      isSvg: true,
    },
    {
      icon: DisposalTicketIcon,
      label: T.DISPOSAL_TICKET,
      description: T.DISPOSAL_TICKET_DESCRIPTION,
      color: theme.palette.grey[700],
      isSvg: true,
    },
    {
      icon: GeneralFeeIcon,
      label: T.ANY_FEE_ADDED,
      description: T.ANY_FEE_ADDED_DESCRIPTION,
      color: theme.palette.grey[700],
      isSvg: true,
    },
    {
      icon: UnassignedRouteIcon,
      label: T.UNASSIGNED_ROUTE,
      description: T.UNASSIGNED_ROUTE_DESCRIPTION,
      color: theme.palette.grey[500],
    },
    {
      icon: RouteAvatar,
      label: T.ASSIGNED_ROUTE,
      description: T.ASSIGNED_ROUTE_DESCRIPTION,
      color: '#66BB6A',
      isAvatar: true,
    },
    {
      icon: WorkOrderNote,
      label: T.WORK_ORDER_NOTES,
      description: T.WORK_ORDER_NOTES_DESCRIPTION,
      color: theme.palette.grey[700],
      isSvg: true,
    },
  ]

  return (
    <>
      <Button
        onClick={handleOpen}
        size="small"
        sx={{
          color: theme.palette.grey[600],
        }}
        variant="text"
        endIcon={<InfoOutlinedIcon />}
      >
        {T.CALENDAR_LEGEND}
      </Button>
      <HHBaseDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <HHDialogTitle title={T.CALENDAR_LEGEND} onClose={handleClose} />
        <DialogContent>
          <Stack spacing={3}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: theme.palette.grey[50],
                overflow: 'hidden',
              }}
            >
              <Box p={2}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {T.WORK_ORDER_STATUSES}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {T.WORK_ORDER_STATUSES_DESCRIPTION}
                </Typography>
              </Box>
              {statusItems.map((item, index) => {
                const IconComponent = item.icon
                const iconElement = item.isSvg ? (
                  <SvgIcon sx={{ fontSize: 16, color: common.white }}>
                    <IconComponent />
                  </SvgIcon>
                ) : (
                  <IconComponent sx={{ fontSize: 16, color: common.white }} />
                )

                const darkenedColor = darken(item.color, 0.3)

                const icon = (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: darkenedColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {iconElement}
                  </Box>
                )

                return (
                  <React.Fragment key={item.label}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2 }}>
                      <Box sx={{ minWidth: '120px', textAlign: 'center' }}>
                        <Chip
                          icon={icon}
                          label={item.label}
                          size="small"
                          sx={{
                            backgroundColor: item.color,
                            border: `1px solid ${theme.palette.divider}`,
                            color: item.textColor,
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', pt: 0.5 }}>
                        {item.description}
                      </Typography>
                    </Box>
                    {index < statusItems.length - 1 && <Divider />}
                  </React.Fragment>
                )
              })}
            </Box>
            <Divider />
            <Box>
              <Typography variant="h6">{T.CALENDAR_ICONS}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ my: 1, fontSize: '0.875rem' }}>
                {T.CALENDAR_ICONS_DESCRIPTION}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2,
                  columnGap: 4,
                }}
              >
                {calendarIcons.map(item => {
                  const IconComponent = item.icon
                  let icon
                  if (item.isAvatar) {
                    icon = (
                      <RouteAvatar
                        name="RN"
                        bgcolor={item.color}
                        sx={{ width: 30, height: 30 }}
                        TypographyProps={{ fontSize: '0.75rem' }}
                      />
                    )
                  } else if (item.isSvg) {
                    icon = (
                      <SvgIcon sx={{ fontSize: 30, color: item.color }}>
                        <IconComponent />
                      </SvgIcon>
                    )
                  } else {
                    icon = <IconComponent sx={{ fontSize: 30, color: item.color }} />
                  }
                  return (
                    <Box key={item.label} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                          pt: 0.25,
                        }}
                      >
                        {icon}
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 0.25 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      </HHBaseDialog>
    </>
  )
}

export default StatusLegend
